import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useBusiness } from '../context/BusinessContext';
import { getCheckoutSessionStatus } from '../services/api';

export default function CheckoutReturnPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentBusiness } = useBusiness();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [customerEmail, setCustomerEmail] = useState<string | null>(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId || !currentBusiness) {
      setStatus('error');
      return;
    }

    const checkStatus = async () => {
      try {
        const result = await getCheckoutSessionStatus(currentBusiness.id, sessionId);

        if (result.status === 'complete') {
          setStatus('success');
          setCustomerEmail(result.customerEmail || null);
        } else {
          setStatus('error');
        }
      } catch (err) {
        console.error('Error checking session status:', err);
        setStatus('error');
      }
    };

    checkStatus();
  }, [searchParams, currentBusiness]);

  const handleContinue = () => {
    navigate('/billing');
  };

  if (status === 'loading') {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircleIcon className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="mt-4 text-2xl font-bold text-gray-900">Payment Successful!</h2>
          <p className="mt-2 text-gray-600">
            Thank you for your payment.
            {customerEmail && ` A confirmation has been sent to ${customerEmail}.`}
          </p>
          <button
            onClick={handleContinue}
            className="mt-6 inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
          >
            Go to Billing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="text-center max-w-md">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <XCircleIcon className="h-10 w-10 text-red-600" />
        </div>
        <h2 className="mt-4 text-2xl font-bold text-gray-900">Payment Incomplete</h2>
        <p className="mt-2 text-gray-600">
          Your payment could not be completed. Please try again or contact support if the issue persists.
        </p>
        <button
          onClick={handleContinue}
          className="mt-6 inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
        >
          Back to Billing
        </button>
      </div>
    </div>
  );
}
